<template>
  <HrPageLayout
    title="Attrition"
    subtitle="Record and manage employee exits (separations)"
    icon="📤"
    :breadcrumb-items="[{ label: 'Home', route: '/dashboard' }, { label: 'Staff Establishment' }, { label: 'Attrition' }]"
  >
    <div class="attrition-module">
      <div class="actions-row">
        <button v-if="permissions.create" type="button" class="hr-btn hr-btn-primary" @click="openForm()">
          Add attrition
        </button>
        <router-link to="/report-attrition" class="hr-btn hr-btn-secondary">Attrition report</router-link>
        <div class="filters-inline">
          <select v-model="filters.departmentId" @change="loadList">
            <option value="">All departments</option>
            <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.departmentName }}</option>
          </select>
          <select v-model="filters.separationType" @change="loadList">
            <option value="">All types</option>
            <option v-for="t in separationTypes" :key="t" :value="t">{{ t }}</option>
          </select>
          <input v-model="filters.dateFrom" type="date" @change="loadList">
          <input v-model="filters.dateTo" type="date" @change="loadList">
          <button type="button" class="hr-btn hr-btn-outline-primary" @click="loadList">Apply</button>
        </div>
      </div>

      <div class="table-card">
        <div v-if="loading" class="text-center py-4">Loading...</div>
        <div v-else class="table-responsive">
          <table class="table table-bordered table-sm">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Type</th>
                <th>Separation date</th>
                <th>Last working day</th>
                <th>Department</th>
                <th>FNF status</th>
                <th>Employee status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in list" :key="r.id">
                <td>
                  <span>{{ r.employeeName || '–' }}</span>
                  <small class="text-muted" v-if="r.employeeId">ID: {{ r.employeeId }}</small>
                </td>
                <td>{{ r.separationType || '–' }}</td>
                <td>{{ formatDate(r.separationDate) }}</td>
                <td>{{ formatDate(r.lastWorkingDay) }}</td>
                <td>{{ r.departmentName || '–' }}</td>
                <td>{{ r.fnfStatus || 'Pending' }}</td>
                <td>{{ r.employee_status || '–' }}</td>
                <td>
                  <button v-if="permissions.manage" type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="openForm(r)">Edit</button>
                  <button v-if="permissions.approve && (r.fnfStatus !== 'Completed')" type="button" class="hr-btn hr-btn-sm hr-btn-success" @click="finalize(r.id)">Finalize</button>
                </td>
              </tr>
              <tr v-if="!list.length"><td colspan="8" class="text-muted text-center py-4">No attrition records</td></tr>
            </tbody>
          </table>
        </div>
        <div v-if="total > limit" class="pagination-row">
          <button type="button" class="hr-btn hr-btn-sm" :disabled="page <= 1" @click="page--; loadList()">Previous</button>
          <span class="px-2">Page {{ page }} of {{ totalPages }}</span>
          <button type="button" class="hr-btn hr-btn-sm" :disabled="page >= totalPages" @click="page++; loadList()">Next</button>
        </div>
      </div>

      <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
        <div class="modal-card">
          <h3>{{ form.id ? 'Edit attrition' : 'Add attrition' }}</h3>
          <div class="form-group">
            <label>Employee <span class="text-danger">*</span></label>
            <select v-model="form.employeeId" :disabled="!!form.id" class="form-control">
              <option value="">Select employee</option>
              <option v-for="emp in activeEmployees" :key="emp.id" :value="emp.id">{{ emp.firstName }} {{ emp.lastName }} ({{ emp.empNo }})</option>
            </select>
          </div>
          <div class="form-group">
            <label>Separation type <span class="text-danger">*</span></label>
            <select v-model="form.separationType" class="form-control">
              <option value="">Select</option>
              <option v-for="t in separationTypes" :key="t" :value="t">{{ t }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>Separation date <span class="text-danger">*</span></label>
            <input v-model="form.separationDate" type="date" class="form-control">
          </div>
          <div class="form-group">
            <label>Last working day</label>
            <input v-model="form.lastWorkingDay" type="date" class="form-control">
          </div>
          <div class="form-group">
            <label>Notes</label>
            <textarea v-model="form.notes" class="form-control" rows="2"></textarea>
          </div>
          <div class="form-group">
            <label>Clearance info</label>
            <textarea v-model="form.clearanceInfo" class="form-control" rows="2"></textarea>
          </div>
          <div class="modal-actions">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showModal = false">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="saveForm">{{ form.id ? 'Update' : 'Create' }}</button>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl } from "@/api";

export default {
  name: "AttritionModule",
  components: { HrPageLayout },
  data() {
    return {
      list: [],
      departments: [],
      activeEmployees: [],
      loading: false,
      saving: false,
      showModal: false,
      permissions: { create: false, manage: false, approve: false },
      total: 0,
      page: 1,
      limit: 50,
      filters: {
        departmentId: "",
        separationType: "",
        dateFrom: "",
        dateTo: ""
      },
      form: {
        id: null,
        employeeId: "",
        separationType: "",
        separationDate: "",
        lastWorkingDay: "",
        notes: "",
        clearanceInfo: ""
      },
      separationTypes: ["Resignation", "Dismissal", "Termination", "Retirement", "Death", "Other"]
    };
  },
  computed: {
    totalPages() {
      return this.limit > 0 ? Math.ceil(this.total / this.limit) : 0;
    }
  },
  created() {
    this.getDepartments();
    this.getActiveEmployees();
    this.loadList();
  },
  methods: {
    formatDate(d) {
      if (!d) return "–";
      return String(d).slice(0, 10);
    },
    getDepartments() {
      const fd = new FormData();
      fd.append("function", "getDepartments");
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success && res.data.data && res.data.data.departments)
          this.departments = res.data.data.departments;
      }).catch(() => {});
    },
    getActiveEmployees() {
      const fd = new FormData();
      fd.append("function", "getEmployees");
      fd.append("status", "Active");
      fd.append("rows_per_page", "2000");
      fd.append("page", "1");
      execute(fd, authUrl).then((res) => {
        const emp = (res.data && res.data.success && res.data.data && res.data.data.employees)
          ? res.data.data.employees
          : [];
        this.activeEmployees = Array.isArray(emp) ? emp : [];
      }).catch(() => { this.activeEmployees = []; });
    },
    loadList() {
      this.loading = true;
      const fd = new FormData();
      fd.append("function", "getAttritionList");
      fd.append("limit", this.limit);
      fd.append("page", this.page);
      if (this.filters.departmentId) fd.append("departmentId", this.filters.departmentId);
      if (this.filters.separationType) fd.append("separationType", this.filters.separationType);
      if (this.filters.dateFrom) fd.append("dateFrom", this.filters.dateFrom);
      if (this.filters.dateTo) fd.append("dateTo", this.filters.dateTo);
      execute(fd, authUrl)
        .then((res) => {
          this.loading = false;
          const payload = (res.data && res.data.success && res.data.data) ? res.data.data : {};
          this.list = payload.data || [];
          this.total = payload.total || 0;
          this.permissions = payload.permissions || {};
        })
        .catch(() => { this.loading = false; this.list = []; });
    },
    openForm(record) {
      if (record) {
        this.form = {
          id: record.id,
          employeeId: record.employeeId,
          separationType: record.separationType || "",
          separationDate: this.formatDate(record.separationDate),
          lastWorkingDay: this.formatDate(record.lastWorkingDay),
          notes: record.notes || "",
          clearanceInfo: record.clearanceInfo || ""
        };
      } else {
        this.form = {
          id: null,
          employeeId: "",
          separationType: "",
          separationDate: "",
          lastWorkingDay: "",
          notes: "",
          clearanceInfo: ""
        };
      }
      this.showModal = true;
    },
    saveForm() {
      if (!this.form.separationDate || !this.form.separationType) {
        alert("Separation type and date are required.");
        return;
      }
      if (!this.form.id && !this.form.employeeId) {
        alert("Please select an employee.");
        return;
      }
      this.saving = true;
      const fd = new FormData();
      fd.append("function", this.form.id ? "updateAttrition" : "createAttrition");
      if (this.form.id) fd.append("id", this.form.id);
      else fd.append("employeeId", this.form.employeeId);
      fd.append("separationType", this.form.separationType);
      fd.append("separationDate", this.form.separationDate);
      fd.append("lastWorkingDay", this.form.lastWorkingDay || this.form.separationDate);
      fd.append("notes", this.form.notes);
      fd.append("clearanceInfo", this.form.clearanceInfo);
      execute(fd, authUrl)
        .then((res) => {
          this.saving = false;
          if (res.data && res.data.success) {
            this.showModal = false;
            this.loadList();
          } else {
            alert(res.data && res.data.message ? res.data.message : "Save failed");
          }
        })
        .catch(() => { this.saving = false; });
    },
    finalize(id) {
      if (!confirm("Finalize this attrition? Employee status will be updated (e.g. Resigned/Terminated).")) return;
      const fd = new FormData();
      fd.append("function", "finalizeAttrition");
      fd.append("id", id);
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success) this.loadList();
        else alert(res.data && res.data.message ? res.data.message : "Finalize failed");
      }).catch(() => {});
    }
  }
};
</script>

<style scoped>
/* ========================================
   ATTRITION MODULE - MODERN UI ENHANCEMENTS
   Theme: Red/Orange (representing exits/departures)
   ======================================== */

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { 
    opacity: 0;
    transform: translateY(20px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

@keyframes shimmer {
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
}

/* Main Container */
.attrition-module { 
  padding: 0 0 24px;
  animation: fadeIn 0.5s ease-in-out;
}

/* Actions Row */
.actions-row { 
  display: flex; 
  flex-wrap: wrap; 
  gap: 12px; 
  margin-bottom: 20px; 
  align-items: center;
  animation: slideUp 0.6s ease-out;
}

/* Filters */
.filters-inline { 
  display: flex; 
  flex-wrap: wrap; 
  gap: 10px; 
  align-items: center; 
  margin-left: auto;
  padding: 12px;
  background: linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.1);
}

.filters-inline select,
.filters-inline input[type="date"] {
  padding: 8px 12px;
  border: 2px solid #fecaca;
  border-radius: 8px;
  font-size: 13px;
  background: white;
  transition: all 0.3s ease;
  min-width: 140px;
}

.filters-inline select:hover,
.filters-inline input[type="date"]:hover {
  border-color: #f87171;
  box-shadow: 0 2px 8px rgba(248, 113, 113, 0.2);
}

.filters-inline select:focus,
.filters-inline input[type="date"]:focus {
  outline: none;
  border-color: #dc2626;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

/* Table Card */
.table-card { 
  background: linear-gradient(135deg, #ffffff 0%, #fef2f2 100%);
  border-radius: 16px; 
  box-shadow: 0 4px 20px rgba(220, 38, 38, 0.08);
  padding: 24px;
  border: 1px solid rgba(220, 38, 38, 0.1);
  animation: slideUp 0.7s ease-out;
  transition: all 0.3s ease;
}

.table-card:hover {
  box-shadow: 0 8px 30px rgba(220, 38, 38, 0.12);
  transform: translateY(-2px);
}

/* Table Styles */
.table-responsive { 
  overflow-x: auto;
  border-radius: 12px;
}

.table { 
  width: 100%; 
  font-size: 13px;
  border-collapse: separate;
  border-spacing: 0;
}

.table thead {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
}

.table thead th {
  padding: 14px 12px;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
  border: none;
  position: sticky;
  top: 0;
  z-index: 10;
}

.table thead th:first-child {
  border-top-left-radius: 12px;
}

.table thead th:last-child {
  border-top-right-radius: 12px;
}

.table tbody tr {
  background: white;
  transition: all 0.3s ease;
  border-bottom: 1px solid #fee2e2;
}

.table tbody tr:hover {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  transform: scale(1.01);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.1);
}

.table tbody td {
  padding: 14px 12px;
  vertical-align: middle;
  border: none;
}

.table tbody tr:last-child td:first-child {
  border-bottom-left-radius: 12px;
}

.table tbody tr:last-child td:last-child {
  border-bottom-right-radius: 12px;
}

/* Employee Info */
.table tbody td:first-child {
  font-weight: 600;
  color: #991b1b;
}

.table tbody td:first-child small {
  display: block;
  margin-top: 4px;
  font-weight: 400;
}

/* Status Badges */
.table tbody td:nth-child(6) {
  font-weight: 600;
}

.table tbody td:nth-child(6)::before {
  content: '●';
  margin-right: 6px;
  font-size: 14px;
}

/* Pending status */
.table tbody tr:has(td:nth-child(6):contains('Pending')) td:nth-child(6) {
  color: #f59e0b;
}

/* Completed status */
.table tbody tr:has(td:nth-child(6):contains('Completed')) td:nth-child(6) {
  color: #10b981;
}

/* Pagination */
.pagination-row { 
  margin-top: 16px; 
  display: flex; 
  align-items: center; 
  justify-content: center;
  gap: 12px;
  padding: 12px;
  background: linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%);
  border-radius: 12px;
  border: 1px solid rgba(220, 38, 38, 0.1);
}

.pagination-row span {
  font-weight: 600;
  color: #991b1b;
  padding: 0 8px;
}

/* Modal Overlay */
.modal-overlay { 
  position: fixed; 
  inset: 0; 
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex; 
  align-items: center; 
  justify-content: center; 
  z-index: 1000;
  animation: fadeIn 0.3s ease-in-out;
}

/* Modal Card */
.modal-card { 
  background: linear-gradient(135deg, #ffffff 0%, #fef2f2 100%);
  border-radius: 20px; 
  padding: 32px; 
  max-width: 520px; 
  width: 90%; 
  max-height: 90vh; 
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(220, 38, 38, 0.3);
  border: 2px solid rgba(220, 38, 38, 0.2);
  animation: slideUp 0.4s ease-out;
}

.modal-card h3 {
  margin: 0 0 24px;
  font-size: 24px;
  font-weight: 700;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Form Groups */
.form-group { 
  margin-bottom: 18px;
  animation: slideUp 0.5s ease-out backwards;
}

.form-group:nth-child(1) { animation-delay: 0.1s; }
.form-group:nth-child(2) { animation-delay: 0.15s; }
.form-group:nth-child(3) { animation-delay: 0.2s; }
.form-group:nth-child(4) { animation-delay: 0.25s; }
.form-group:nth-child(5) { animation-delay: 0.3s; }
.form-group:nth-child(6) { animation-delay: 0.35s; }

.form-group label { 
  display: block; 
  font-weight: 600; 
  margin-bottom: 8px;
  color: #991b1b;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-control { 
  width: 100%; 
  padding: 12px 16px; 
  border: 2px solid #fecaca; 
  border-radius: 10px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: white;
}

.form-control:hover {
  border-color: #f87171;
  box-shadow: 0 2px 8px rgba(248, 113, 113, 0.2);
}

.form-control:focus {
  outline: none;
  border-color: #dc2626;
  box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.1);
  transform: translateY(-1px);
}

textarea.form-control {
  resize: vertical;
  min-height: 80px;
}

/* Modal Actions */
.modal-actions { 
  display: flex; 
  gap: 12px; 
  justify-content: flex-end; 
  margin-top: 24px;
  padding-top: 20px;
  border-top: 2px solid #fee2e2;
}

/* Text Utilities */
.text-muted { 
  color: #9ca3af; 
  font-size: 12px; 
}

.text-center { 
  text-align: center; 
}

.py-4 { 
  padding-top: 24px; 
  padding-bottom: 24px; 
}

.text-danger {
  color: #dc2626;
  font-weight: 700;
}

/* Button Styles */
.hr-btn { 
  padding: 10px 20px; 
  border-radius: 10px; 
  cursor: pointer; 
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  border: none;
  position: relative;
  overflow: hidden;
  text-decoration: none;
  display: inline-block;
}

.hr-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.hr-btn:hover::before {
  width: 300px;
  height: 300px;
}

.hr-btn:active {
  transform: scale(0.95);
}

.hr-btn-sm { 
  padding: 6px 14px; 
  font-size: 12px;
  border-radius: 8px;
}

.hr-btn-primary { 
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.hr-btn-primary:hover {
  background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
  box-shadow: 0 6px 20px rgba(220, 38, 38, 0.4);
  transform: translateY(-2px);
}

.hr-btn-secondary { 
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(107, 114, 128, 0.3);
}

.hr-btn-secondary:hover {
  background: linear-gradient(135deg, #4b5563 0%, #374151 100%);
  box-shadow: 0 6px 20px rgba(107, 114, 128, 0.4);
  transform: translateY(-2px);
}

.hr-btn-success { 
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.hr-btn-success:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
  transform: translateY(-2px);
}

.hr-btn-outline-primary { 
  background: white;
  border: 2px solid #dc2626; 
  color: #dc2626;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.1);
}

.hr-btn-outline-primary:hover {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  box-shadow: 0 4px 16px rgba(220, 38, 38, 0.3);
  transform: translateY(-2px);
}

.hr-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

.hr-btn:disabled:hover {
  transform: none !important;
  box-shadow: none !important;
}

/* Loading State */
.text-center.py-4 {
  font-size: 16px;
  color: #991b1b;
  font-weight: 600;
  animation: pulse 1.5s ease-in-out infinite;
}

/* Responsive Design */
@media (max-width: 768px) {
  .actions-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filters-inline {
    margin-left: 0;
    flex-direction: column;
    width: 100%;
  }
  
  .filters-inline select,
  .filters-inline input[type="date"] {
    width: 100%;
  }
  
  .table-card {
    padding: 16px;
  }
  
  .modal-card {
    padding: 24px;
    width: 95%;
  }
  
  .modal-actions {
    flex-direction: column;
  }
  
  .hr-btn {
    width: 100%;
    text-align: center;
  }
}

/* Scrollbar Styling */
.table-responsive::-webkit-scrollbar,
.modal-card::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.table-responsive::-webkit-scrollbar-track,
.modal-card::-webkit-scrollbar-track {
  background: #fee2e2;
  border-radius: 10px;
}

.table-responsive::-webkit-scrollbar-thumb,
.modal-card::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  border-radius: 10px;
}

.table-responsive::-webkit-scrollbar-thumb:hover,
.modal-card::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
}
</style>
